import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Wallet, ArrowsClockwise, Clock, Hourglass } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending_confirmation: { label: 'Awaiting pro response', icon: Hourglass, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-300' },
  confirmed:            { label: 'Confirmed',              icon: Check,      color: 'text-green-700', bg: 'bg-green-50 border-green-300' },
  declined:             { label: 'Declined by pro',        icon: X,          color: 'text-red-700',   bg: 'bg-red-50 border-red-300' },
  auto_declined:        { label: 'No response (auto-declined)', icon: Clock, color: 'text-red-700',   bg: 'bg-red-50 border-red-300' },
  refunded:             { label: 'Refunded to wallet',     icon: Wallet,     color: 'text-blue-700',  bg: 'bg-blue-50 border-blue-300' },
  replacement_pending:  { label: 'Replacement needed',     icon: ArrowsClockwise, color: 'text-amber-800', bg: 'bg-amber-50 border-amber-400' },
};

/**
 * Per-booking slot resolution card on the Customer Dashboard.
 *
 * For each pro line, shows the current status. When a slot is declined the
 * customer chooses either:
 *   • Refund to wallet (instant credit, no Razorpay roundtrip)
 *   • Pick replacement — opens a category-filtered picker dialog and fires
 *     a fresh confirmation call to the new pro (unlimited attempts).
 */
const CustomerBookingProSlots = ({ booking, onChange }) => {
  const [slots, setSlots] = useState([]);
  const [marriageType, setMarriageType] = useState(null);
  const [eventType, setEventType] = useState(null);
  const [eventDate, setEventDate] = useState(null);
  const [picker, setPicker] = useState({ open: false, slot: null, candidates: [], loading: false });
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/customer/professional-bookings/${booking._id || booking.id}/slots`,
        { withCredentials: true });
      setSlots(data?.slots || []);
      setMarriageType(data?.marriage_type || null);
      setEventType(data?.event_type || null);
      setEventDate(data?.event_date || null);
    } catch (err) {
      // silent — booking may not have dispatched yet
    }
  }, [booking._id, booking.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const handleRefund = async (slot) => {
    if (!window.confirm(`Refund ₹${slot.amount} to your wallet for "${slot.name}"?`)) return;
    setBusy(slot.slot_id);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/customer/professional-bookings/${booking._id || booking.id}/slots/${slot.slot_id}/refund`,
        {}, { withCredentials: true });
      toast.success(`₹${data.credited_to_wallet} credited to your wallet`);
      refresh(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refund failed');
    }
    setBusy(null);
  };

  const openPicker = async (slot) => {
    setPicker({ open: true, slot, candidates: [], loading: true });
    try {
      const params = new URLSearchParams();
      if (eventType) params.append('event_type', eventType);
      if (eventDate) params.append('event_date', eventDate);
      if (slot.category) params.append('category', slot.category);
      const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/pros?${params.toString()}`);
      // server returns { slug: [pro_dict] } shape
      const pool = (data && data[slot.category]) || [];
      // exclude the original declined pro + any in slot.history
      const declinedIds = new Set([
        slot.pro_user_id,
        ...(slot.history || []).map(h => h.pro_user_id).filter(Boolean),
      ]);
      const candidates = pool.filter(p => !declinedIds.has(p.id));
      setPicker({ open: true, slot, candidates, loading: false });
    } catch (err) {
      toast.error('Could not load replacement options');
      setPicker({ open: false, slot: null, candidates: [], loading: false });
    }
  };

  const handlePick = async (proUserId, proName) => {
    if (!picker.slot) return;
    setBusy(picker.slot.slot_id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/customer/professional-bookings/${booking._id || booking.id}/slots/${picker.slot.slot_id}/replace`,
        { new_pro_user_id: proUserId, new_pro_name: proName }, { withCredentials: true });
      toast.success('Replacement requested · confirmation call sent');
      setPicker({ open: false, slot: null, candidates: [], loading: false });
      refresh(); onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Replacement failed');
    }
    setBusy(null);
  };

  if (!slots.length) return null;

  return (
    <Card className="border-[#E8E1D5] mb-4" data-testid={`booking-slots-${booking._id || booking.id}`}>
      <CardHeader className="border-b border-[#E8E1D5] py-3">
        <CardTitle className="text-base font-heading">Professional Confirmations</CardTitle>
        <CardDescription className="text-xs">
          {slots.filter(s => s.status === 'confirmed').length} of {slots.length} confirmed
          {marriageType && <> · {marriageType} wedding</>}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-3 space-y-2">
        {slots.map(slot => {
          const meta = STATUS_META[slot.status] || STATUS_META.pending_confirmation;
          const Icon = meta.icon;
          const needsAction = ['declined', 'auto_declined', 'replacement_pending'].includes(slot.status);
          return (
            <div key={slot.slot_id} className={`border-2 rounded p-3 ${meta.bg}`} data-testid={`slot-${slot.slot_id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">{slot.name}</div>
                  <div className={`text-xs ${meta.color} flex items-center gap-1 mt-0.5 font-bold`}>
                    <Icon size={12} weight="bold" /> {meta.label}
                  </div>
                  {slot.decline_reason && slot.status !== 'refunded' && (
                    <div className="text-[11px] text-text-secondary mt-1 italic">"{slot.decline_reason}"</div>
                  )}
                  {(slot.history || []).length > 0 && (
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-brand-primary text-sand-50 rounded"
                        data-testid={`slot-attempts-${slot.slot_id}`}>
                        Attempts · {slot.history.length}
                      </span>
                      <details className="text-[10px] text-text-secondary">
                        <summary className="cursor-pointer hover:text-text-primary">View history</summary>
                        <ul className="mt-1 ml-2 list-disc space-y-0.5">
                          {(slot.history || []).map((h, hi) => (
                            <li key={hi}>
                              <span className="font-medium text-text-primary">{h.pro_display_name || 'Pro'}</span>
                              {' — '}{h.status || 'declined'}
                              {h.decline_reason ? <span className="italic"> ("{h.decline_reason}")</span> : null}
                            </li>
                          ))}
                        </ul>
                      </details>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-text-primary">₹{Number(slot.amount).toLocaleString('en-IN')}</div>
                </div>
              </div>
              {needsAction && (
                <div className="flex items-center gap-2 mt-2">
                  <Button onClick={() => handleRefund(slot)} disabled={busy === slot.slot_id}
                    size="sm" variant="outline"
                    className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50 rounded-none"
                    data-testid={`slot-refund-${slot.slot_id}`}>
                    <Wallet size={12} className="mr-1" /> Refund to wallet
                  </Button>
                  <Button onClick={() => openPicker(slot)} disabled={busy === slot.slot_id}
                    size="sm"
                    className="flex-1 bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none"
                    data-testid={`slot-replace-${slot.slot_id}`}>
                    <ArrowsClockwise size={12} className="mr-1" /> Pick replacement
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      {picker.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setPicker({ ...picker, open: false }); }}
          data-testid="replacement-picker-modal">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="border-b border-[#E8E1D5] px-5 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-xl">Pick replacement</h3>
                <p className="text-xs text-text-secondary">{picker.slot?.category_label || picker.slot?.category}</p>
              </div>
              <button onClick={() => setPicker({ ...picker, open: false })}
                className="p-2 rounded-full hover:bg-sand-100">
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {picker.loading && <p className="text-sm text-text-secondary text-center py-6">Loading options…</p>}
              {!picker.loading && picker.candidates.length === 0 && (
                <p className="text-sm text-text-secondary text-center py-6">
                  No replacement available for this category on your event date.
                  Use "Refund to wallet" instead.
                </p>
              )}
              {!picker.loading && picker.candidates.map(p => (
                <div key={p.id} className="border-2 border-[#E8E1D5] rounded p-3 flex items-center justify-between gap-3 hover:border-brand-primary/40 transition-colors"
                  data-testid={`replacement-option-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{p.name}</div>
                    <div className="text-[11px] text-text-secondary">
                      {p.location || '—'}
                      {p.experience_years ? ` · ${p.experience_years} yrs` : ''}
                      {p.effective_price ? ` · ₹${Number(p.effective_price).toLocaleString('en-IN')}` : ''}
                    </div>
                  </div>
                  <Button onClick={() => handlePick(p.id, p.name)} disabled={busy === picker.slot.slot_id}
                    size="sm" className="bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none flex-shrink-0"
                    data-testid={`replacement-pick-${p.id}`}>
                    Pick
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default CustomerBookingProSlots;
